
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Crown, AlertTriangle, Star, CheckCircle, Eye, Target } from 'lucide-react';

const ProfessionalPositioningSection = () => {
  const { isMobile } = useBreakpoint();

  const amateurSignals = [
    "When your landing page looks outdated...",
    "When you stumble through an unpracticed pitch...",
    "When you don't have professional examples..."
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-white via-[#FEE6D8]/10 to-white" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
      backgroundPattern="dots"
      patternColor="#003a70"
      patternOpacity={3}
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#003a70] to-[#003a70]/90 rounded-full shadow-lg">
            <Crown className="text-white" size={isMobile ? 24 : 28} />
            <span className="text-white font-bold text-lg md:text-xl">Professional Positioning</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            It's About 
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              Professional Positioning
            </span>
          </h2>
        </div>

        {/* Problem Statement */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="max-w-4xl mx-auto">
            <p className={cn(
              "text-[#003a70]/90 leading-relaxed mb-8",
              isMobile ? "text-lg" : "text-xl lg:text-2xl"
            )}>
              When you show up with amateur-looking materials, prospects notice.
            </p>
            
            {/* Amateur Signals */}
            <div className="bg-gradient-to-br from-[#FEE6D8]/30 to-[#FEE6D8]/15 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/40 shadow-xl relative overflow-hidden">
              {/* Warning accent */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#003a70] to-[#003a70]/80"></div>
              
              <div className="space-y-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-16 h-16 bg-[#003a70] rounded-full flex items-center justify-center shadow-lg">
                    <AlertTriangle className="text-white" size={28} />
                  </div>
                </div>
                
                <div className="space-y-4">
                  {amateurSignals.map((signal, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-4 p-4 bg-white/60 rounded-xl border border-[#FEE6D8]/50"
                    >
                      <div className="text-2xl mt-1">👉</div>
                      <p className={cn(
                        "text-[#003a70] font-medium flex-1",
                        isMobile ? "text-base" : "text-lg"
                      )}>
                        {signal}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Problem Impact */}
        <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-2xl p-8 md:p-10 border border-[#FEE6D8]/60 shadow-lg max-w-4xl mx-auto">
            <div className="text-center">
              <Eye className="text-[#003a70] mx-auto mb-4" size={48} />
              <p className={cn(
                "text-[#003a70] font-bold leading-relaxed",
                isMobile ? "text-xl" : "text-2xl lg:text-3xl"
              )}>
                You look like everyone else trying to "wing it."
              </p>
            </div>
          </div>
        </div>

        {/* Solution Contrast */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="max-w-4xl mx-auto">
            <p className={cn(
              "text-[#003a70]/90 leading-relaxed mb-8",
              isMobile ? "text-lg" : "text-xl lg:text-2xl"
            )}>
              But when you have polished, professional materials?
            </p>
            
            {/* Professional Impact */}
            <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/60 shadow-xl relative overflow-hidden">
              {/* Success accent */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#003a70] to-[#003a70]/80"></div>
              
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                    <Crown className="text-white" size={32} />
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="text-4xl" role="img" aria-label="Cool face">😎</div>
                  <p className={cn(
                    "text-[#003a70] font-bold leading-tight",
                    isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
                  )}>
                    You instantly become the expert.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Powerful Statement */}
        <div className="space-y-4 animate-fade-in" style={{ animationDelay: '0.8s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/40 via-white to-[#003a70]/10 rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/60 shadow-2xl max-w-5xl mx-auto relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-4 right-4 opacity-10">
              <Star className="text-[#003a70]" size={48} />
            </div>
            <div className="absolute bottom-4 left-4 opacity-10">
              <Target className="text-[#003a70]" size={48} />
            </div>
            
            <div className="relative z-10 text-center space-y-6">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle className="text-white" size={28} />
                </div>
              </div>
              
              <p className={cn(
                "text-[#003a70] font-bold leading-relaxed",
                isMobile ? "text-lg md:text-xl" : "text-xl lg:text-2xl"
              )}>
                Even if you just started offering this service
              </p>
              
              <p className={cn(
                "bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent font-bold leading-tight",
                isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl"
              )}>
                yesterday.
              </p>
              
              <div className="mt-8 p-6 bg-white/80 backdrop-blur-sm rounded-xl border border-[#FEE6D8]/50 shadow-inner">
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-base" : "text-lg"
                )}>
                  Professional materials don't just make you look good - 
                  <span className="font-bold text-[#003a70]"> they make you credible instantly.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default ProfessionalPositioningSection;
